/**
 * `commerce.catalog` — data schemas (K1). Money is ALWAYS an integer amount
 * in MINOR units plus an ISO-4217 currency code — never a float, never a
 * display string (unlike venue.menu-hours' price, which is deliberately just
 * what the owner writes) — because commerce needs to total and charge these
 * amounts later (K2/K3).
 *
 * INTEGER-MINOR-UNITS CONTRACT (binding on K2/K3): every amount here is a
 * whole count of minor units. All downstream totaling, tax, and charge math
 * MUST stay in integer minor units (add/subtract integers, format only at the
 * very edge) — never sum floats of major units. `MAX_AMOUNT_MINOR` bounds each
 * amount so a sum of a full catalog stays well inside Number.MAX_SAFE_INTEGER
 * and no amount can silently lose integer precision.
 */
import { z } from 'zod';
import { ImageValue, Slug } from '../../schema/types.js';

/**
 * Upper bound for any single amount, in minor units (1e12 = ten billion major
 * units at a 2-digit currency). Large enough for any real price or gift card,
 * small enough that even a 200-product catalog sums far below
 * Number.MAX_SAFE_INTEGER (~9.0e15), so integer math never overflows.
 */
export const MAX_AMOUNT_MINOR = 1_000_000_000_000;

/** An amount of money: an integer count of minor units (cents, haléře, ...)
 *  plus its ISO-4217 currency code. Define once, reuse everywhere a price is
 *  needed — never a float, never a pre-formatted display string. Bounded by
 *  MAX_AMOUNT_MINOR so it stays an exact integer through all K2/K3 totaling. */
export const Money = z.object({
  amountMinor: z.number().int().min(0).max(MAX_AMOUNT_MINOR),
  currency: z.string().regex(/^[A-Z]{3}$/, 'must be an ISO-4217 currency code'),
}).strict();

/** A gift card's buyer-selectable amount: fixed presets, a free-pick range, or
 *  both. Every amount is a positive integer minor-unit value, bounded like any
 *  other price; a preset list, if present, must be non-empty. */
export const GiftCardOptions = z.object({
  presetAmountsMinor: z.array(z.number().int().positive().max(MAX_AMOUNT_MINOR)).min(1).max(12).optional(),
  customRange: z.object({
    minMinor: z.number().int().positive().max(MAX_AMOUNT_MINOR),
    maxMinor: z.number().int().positive().max(MAX_AMOUNT_MINOR),
  }).strict().refine((range) => range.minMinor <= range.maxMinor, 'gift card customRange minMinor must be <= maxMinor').optional(),
}).strict();

export const ProductKind = z.enum(['standard', 'gift_card']);

/** True when a giftCard block carries a real buyer choice (a non-empty preset
 *  list or a custom range). An empty `{}` giftCard is NOT a valid gift card. */
function hasGiftCardChoice(giftCard: z.infer<typeof GiftCardOptions> | undefined): boolean {
  if (!giftCard) return false;
  return (giftCard.presetAmountsMinor?.length ?? 0) > 0 || giftCard.customRange !== undefined;
}

export const Product = z.object({
  id: Slug,
  name: z.string().min(1).max(160),
  description: z.string().max(600).optional(),
  price: Money,
  image: ImageValue.optional(),
  available: z.boolean().default(true),
  sku: z.string().max(64).optional(),
  /** A gift card is a product whose amount the buyer picks from presets or a range. */
  kind: ProductKind.default('standard'),
  giftCard: GiftCardOptions.optional(),
}).strict().superRefine((product, ctx) => {
  // kind and giftCard must agree: a gift card needs a real buyer choice; a
  // standard product must not carry gift-card options at all.
  if (product.kind === 'gift_card' && !hasGiftCardChoice(product.giftCard)) {
    ctx.addIssue('a gift_card product requires giftCard with at least one preset amount or a custom range');
  }
  if (product.kind === 'standard' && product.giftCard !== undefined) {
    ctx.addIssue('a standard product must not carry giftCard options');
  }
});

export const CommerceCatalogData = z.object({
  products: z.array(Product).max(200),
  /** Catalog-level default currency. Every product's price.currency MUST match
   *  it — enforced both here (so a hand-seeded snapshot with a mismatched
   *  product fails validateSnapshotAgainstDefinition/saveDraft) and, for an
   *  earlier and clearer error, in the addProduct/updateProduct operations. */
  currency: z.string().regex(/^[A-Z]{3}$/, 'must be an ISO-4217 currency code'),
}).strict().refine(
  (data) => data.products.every((product) => product.price.currency === data.currency),
  'every product price currency must equal the catalog currency',
).refine(
  // Product ids are the cart identifiers a storefront + checkout use, so they
  // MUST be unique: a duplicate id would let a buyer select one card but be
  // priced by the FIRST match (a .find()), persisting a different price/
  // availability than displayed. Reject the ambiguity at the catalog contract.
  (data) => new Set(data.products.map((product) => product.id)).size === data.products.length,
  'product ids must be unique within a catalog',
);

export const CommerceCatalogConfig = z.object({
  /** Card density for `product-grid`. Defaults to a full grid. */
  view: z.enum(['grid', 'list']).default('grid'),
  columns: z.number().int().min(1).max(4).optional(),
  /** Locale for money formatting in rendered views (mirrors
   *  venue.menu-hours). Defaults to Czech — the primary cs/pl market. */
  locale: z.enum(['cs', 'en', 'pl']).default('cs'),
  /** Which product `product-detail` renders. */
  productId: Slug.optional(),
});

export type MoneyT = z.infer<typeof Money>;
export type GiftCardOptionsT = z.infer<typeof GiftCardOptions>;
export type ProductKindT = z.infer<typeof ProductKind>;
export type ProductT = z.infer<typeof Product>;
export type CommerceCatalogDataT = z.infer<typeof CommerceCatalogData>;
export type CommerceCatalogConfigT = z.infer<typeof CommerceCatalogConfig>;
